import { useCallback, useRef, useState } from 'react';
import { useAccessToken } from '@/auth/useAccessToken';
import { attachmentsApi } from './attachmentsApi';

export interface PendingAttachment {
  key: string;          // stable local key
  id?: string;          // server id once uploaded
  fileName: string;
  sizeBytes: number;
  contentType: string;
  status: 'uploading' | 'done' | 'error';
}

let counter = 0;

/**
 * Manages staged attachment uploads for a composer. Files upload immediately
 * (so the create/reply call only needs to send their ids). Images can be
 * flagged inline. Returns the done ids + an `uploading` flag for submit-gating.
 */
export function useAttachmentUploads() {
  const getAccessToken = useAccessToken();
  const [items, setItems] = useState<PendingAttachment[]>([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const addFiles = useCallback(
    (files: File[] | FileList, inline = false) => {
      const list = Array.from(files);
      for (const file of list) {
        const key = `att-${++counter}`;
        setItems((prev) => [
          ...prev,
          { key, fileName: file.name, sizeBytes: file.size, contentType: file.type, status: 'uploading' }
        ]);
        attachmentsApi
          .upload(file, inline, getAccessToken)
          .then((res) =>
            setItems((prev) =>
              prev.map((it) => (it.key === key ? { ...it, id: res.id, status: 'done' } : it))
            )
          )
          .catch(() =>
            setItems((prev) => prev.map((it) => (it.key === key ? { ...it, status: 'error' } : it)))
          );
      }
    },
    [getAccessToken]
  );

  const remove = useCallback((key: string) => setItems((prev) => prev.filter((it) => it.key !== key)), []);
  const reset = useCallback(() => setItems([]), []);

  return {
    items,
    addFiles,
    remove,
    reset,
    ids: items.filter((it) => it.status === 'done' && it.id).map((it) => it.id!),
    uploading: items.some((it) => it.status === 'uploading')
  };
}

/** Pull image File objects out of a clipboard/drop event's items. */
export function imageFilesFrom(data: DataTransfer | null): File[] {
  if (!data) return [];
  const out: File[] = [];
  for (const item of Array.from(data.items ?? [])) {
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const f = item.getAsFile();
      if (f) out.push(f);
    }
  }
  return out;
}
